/*
  The filters of the live view and the family navigator: by state and by person (ui-direction screen 5).
  A filter changes only what the screen draws. It hides nothing from the files. What does not match is dimmed,
  and the nodes above a match stay as they are, so the family path of a match can still be read.
*/

import type { Id } from "../../../api/types";
import { ancestorsOf, type NavModel } from "./nav";
import type { DisplayState } from "./state";

export interface NavFilters {
  state: "all" | DisplayState;
  /** The name of a person, as the record gives it. */
  person: "all" | string;
}

export const NO_FILTERS: NavFilters = { state: "all", person: "all" };

export function isFiltering(filters: NavFilters): boolean {
  return filters.state !== "all" || filters.person !== "all";
}

export interface Highlight {
  /** The nodes that match every filter. */
  match: Set<Id>;
  /** The nodes above a match: drawn as usual, not dimmed. */
  context: Set<Id>;
}

/**
 * What the filters pick out. Null when no filter is set (everything is drawn as usual).
 * `touched` is the set of elements the chosen person worked on; it is needed when a person is chosen.
 */
export function highlightFor(model: NavModel, filters: NavFilters, touched: ReadonlySet<Id> | null): Highlight | null {
  if (!isFiltering(filters)) return null;
  const match = new Set<Id>();
  for (const node of model.nodes) {
    if (filters.state !== "all" && node.state !== filters.state) continue;
    if (filters.person !== "all" && !(touched?.has(node.id) ?? false)) continue;
    match.add(node.id);
  }
  const context = new Set<Id>();
  for (const id of match) for (const ancestor of ancestorsOf(model, id)) if (!match.has(ancestor.id)) context.add(ancestor.id);
  return { match, context };
}

/** True when a node is dimmed under a highlight. */
export function isDimmed(highlight: Highlight | null, id: Id): boolean {
  return !!highlight && !highlight.match.has(id) && !highlight.context.has(id);
}

/** The ancestors that have to be open to show every match. */
export function idsToOpen(model: NavModel, highlight: Highlight | null): Set<Id> {
  const out = new Set<Id>();
  if (!highlight) return out;
  for (const id of highlight.match) for (const ancestor of ancestorsOf(model, id)) out.add(ancestor.id);
  return out;
}
